import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Box,
  Breadcrumbs,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  Link as MuiLink,
} from "@mui/material";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import DeliveryDiningOutlinedIcon from "@mui/icons-material/DeliveryDiningOutlined";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaymentIcon from "@mui/icons-material/Payment";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import useRazorpay from "react-razorpay";

import CustomisedSnackbar from "../../components/common/Snackbar/CustomisedSnackbar";
import Loader from "../../components/loader/Loader";
import UserDataAPI from "../../api/firebase/UserDataAPI";
import OrdersAPI from "../../api/firebase/OrdersAPI";
import UserProfileContext from "../../context/userProfile/UserProfileContext";
import CartItemsContext from "../../context/cartItems/CartItemsContext";
import {
  formatDateToCustomFormat,
  getDateFromCreatedAt,
} from "../../utils/dateUtils";

const BRAND_COLOR = process.env.REACT_APP_BG_COLOR || "#075e54";
const WHATSAPP_PHONE = "919411174186";

const OrderDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [Razorpay] = useRazorpay();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const userDataApi = useMemo(() => new UserDataAPI(), []);
  const ordersAPI = useMemo(() => new OrdersAPI(), []);
  const userProfileContext = useContext(UserProfileContext);
  const cartItemsContext = useContext(CartItemsContext);

  // Order state initialized safely from location.state?.order or null
  const [order, setOrder] = useState(location.state?.order || null);
  const [fetchingOrder, setFetchingOrder] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [openHelpDialog, setOpenHelpDialog] = useState(false);
  const [copiedKey, setCopiedKey] = useState("");

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    title: "",
    severity: "success",
  });

  const showNotification = (message, title = "Notification", severity = "success") => {
    setSnackbar({
      open: true,
      message,
      title,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Determine Order ID from state or URL query
  const targetOrderId = location.state?.order?.id || searchParams.get("id") || searchParams.get("orderId");

  // Fetch or refresh order details
  useEffect(() => {
    let isMounted = true;
    const fetchOrderData = async () => {
      if (!targetOrderId) return;
      if (!order) {
        setFetchingOrder(true);
      }
      try {
        const freshOrder = await ordersAPI.getOrderDetailById(targetOrderId);
        if (isMounted && freshOrder) {
          setOrder((prev) => ({
            ...(prev || {}),
            ...freshOrder,
            id: freshOrder.id || targetOrderId,
          }));
        }
      } catch (err) {
        console.error("Failed to load order details:", err);
      } finally {
        if (isMounted) {
          setFetchingOrder(false);
        }
      }
    };

    fetchOrderData();
    return () => {
      isMounted = false;
    };
  }, [targetOrderId]);

  // Copy helper
  const handleCopy = (text, key, label) => {
    if (!text) return;
    navigator.clipboard?.writeText?.(String(text));
    setCopiedKey(key);
    showNotification(`${label} copied to clipboard!`, "Copied", "success");
    setTimeout(() => {
      setCopiedKey("");
    }, 2000);
  };

  // Check if payment is still under processing window (5 mins)
  const isPaymentProcessing = (ord) => {
    if (!ord) return false;
    if (ord?.payment?.modalClosed === true) {
      return false;
    }
    const orderAt = getDateFromCreatedAt(ord.createdAt);
    const nowDate = new Date();
    const diff = (nowDate - orderAt) / 60000;
    return diff < 5;
  };

  // Payment Update Handler
  const handlePaymentUpdate = ({ success, response }) => {
    setLoadingAction(true);
    if (success) {
      const payment_id = response.razorpay_payment_id;
      const dataForOrderTable = {
        payload: {
          payment: {
            method: "online",
            status: "completed",
            payment_id: payment_id,
            attempted_at: new Date().toString(),
          },
          status: "Placed",
        },
        id: order.id,
      };

      ordersAPI.setOrder(dataForOrderTable).then(() => {
        const user = userProfileContext.state;
        if (user && user.id) {
          const prevOrders = Array.isArray(user.orders) ? user.orders : [];
          const updatedOrders = prevOrders.map((o) => {
            if (o.id === order.id) {
              return { ...o, ...dataForOrderTable.payload };
            }
            return o;
          });

          const dataForUsersTable = {
            id: user.id,
            payload: { orders: [...updatedOrders] },
          };

          userDataApi.addUserData(dataForUsersTable).then((res) => {
            if (res.success) {
              userProfileContext.setState((prev) => ({
                ...prev,
                cartItems: [],
                couponApplied: null,
                orders: dataForUsersTable.payload.orders,
              }));
              navigate("/orderPlaced");
            }
          });
        } else {
          navigate("/orderPlaced");
        }
      });
    } else {
      setLoadingAction(false);
      showNotification(
        response?.error?.description || "Payment was not successful. Please try again.",
        "Payment Failed",
        "error"
      );
    }
  };

  // Retry Razorpay Payment
  const retryPayment = () => {
    if (!order) return;
    const user = userProfileContext.state || {};

    const options = {
      key:
        process.env.REACT_APP_PAYMENT_METHOD === "TEST"
          ? process.env.REACT_APP_RAZORPAY_KEY_TEST
          : process.env.REACT_APP_RAZORPAY_KEY_LIVE,
      amount: order.amount * 100,
      currency: "INR",
      name: "Inproveda",
      description: "Order #" + order.id,
      order_id: order.id,
      handler: (response) => {
        handlePaymentUpdate({ success: true, response });
      },
      prefill: {
        name: user.displayName || order.address?.name || "",
        email: user.email || order.address?.email || "",
        contact: user.mobile || order.address?.mobile || "",
      },
      notes: {
        address: order.address?.address || "Inproveda Order",
      },
      theme: {
        color: BRAND_COLOR,
      },
      modal: {
        ondismiss: function () {
          console.log("Payment modal closed by the user.");
        },
      },
    };

    const rzp1 = new Razorpay(options);
    rzp1.on("payment.failed", function (response) {
      setLoadingAction(false);
      handlePaymentUpdate({ success: false, response });
    });
    rzp1.open();
  };

  // Buy Again handler
  const handleBuyAgain = (item) => {
    if (!item) return;
    const prevCart = Array.isArray(cartItemsContext.state) ? [...cartItemsContext.state] : [];
    const existingIdx = prevCart.findIndex((c) => c.id === item.id);

    if (existingIdx > -1) {
      prevCart[existingIdx] = {
        ...prevCart[existingIdx],
        quantity: (prevCart[existingIdx].quantity || 1) + 1,
      };
    } else {
      prevCart.push({
        ...item,
        quantity: 1,
      });
    }

    cartItemsContext.setState(prevCart);
    showNotification(`Added "${item.title}" to your cart.`, "Added to Cart", "success");
  };

  // Calculate Status & Steps
  const statusMeta = useMemo(() => {
    if (!order) return null;
    const statusRaw = (order.status || "").trim().toUpperCase();
    const createdDate = getDateFromCreatedAt(order.createdAt);
    const todayStr = new Date().toDateString();
    const createdStr = new Date(createdDate).toDateString();
    const isToday = todayStr === createdStr;

    let labelCreatedAt = isToday
      ? "Confirmed Today"
      : `Confirmed on ${formatDateToCustomFormat(createdDate)}`;

    const shipEstDate = new Date(createdDate);
    shipEstDate.setDate(shipEstDate.getDate() + 2);
    const expectedSD = formatDateToCustomFormat(shipEstDate);

    const delivEstDate = new Date(createdDate);
    delivEstDate.setDate(delivEstDate.getDate() + 6);
    const expectedDD = formatDateToCustomFormat(delivEstDate);

    switch (statusRaw) {
      case "CREATED": {
        const isProcessing = isPaymentProcessing(order);
        return {
          label: isProcessing ? "Payment Processing" : "Payment Pending",
          badgeColor: isProcessing ? "#b45309" : "#b91c1c",
          badgeBg: isProcessing ? "#fffbeb" : "#fef2f2",
          badgeBorder: isProcessing ? "#fde68a" : "#fecaca",
          activeStep: 0,
          expectedBanner: isProcessing
            ? "Your payment is being processed. It will update shortly."
            : "Complete payment to confirm and initiate shipping for this order.",
          steps: [
            {
              label: isProcessing ? "Processing Payment" : "Payment Pending",
              subtitle: isProcessing ? "Verifying with bank" : "Awaiting payment",
              completed: false,
              active: true,
              icon: <PaymentIcon />,
            },
            {
              label: "Order Confirmed",
              subtitle: "Awaiting payment",
              completed: false,
              active: false,
              icon: <CheckCircleRoundedIcon />,
            },
          ],
        };
      }

      case "PLACED":
        return {
          label: "Order Confirmed",
          badgeColor: "#047857",
          badgeBg: "#ecfdf5",
          badgeBorder: "#a7f3d0",
          activeStep: 0,
          expectedBanner: `Estimated Delivery by ${expectedDD}`,
          steps: [
            {
              label: "Confirmed",
              subtitle: labelCreatedAt,
              completed: true,
              active: false,
              icon: <CheckCircleRoundedIcon />,
            },
            {
              label: "Shipped",
              subtitle: `Expected by ${expectedSD}`,
              completed: false,
              active: true,
              icon: <LocalShippingOutlinedIcon />,
            },
            {
              label: "Out for Delivery",
              subtitle: `Expected by ${expectedDD}`,
              completed: false,
              active: false,
              icon: <DeliveryDiningOutlinedIcon />,
            },
            {
              label: "Delivered",
              subtitle: `Expected by ${expectedDD}`,
              completed: false,
              active: false,
              icon: <TaskAltRoundedIcon />,
            },
          ],
        };

      case "SHIPPED": {
        const actualShipped = order.shippedAt
          ? formatDateToCustomFormat(getDateFromCreatedAt(order.shippedAt))
          : expectedSD;
        return {
          label: "Shipped",
          badgeColor: "#0284c7",
          badgeBg: "#f0f9ff",
          badgeBorder: "#bae6fd",
          activeStep: 1,
          expectedBanner: `Package is in transit. Estimated delivery: ${expectedDD}`,
          steps: [
            {
              label: "Confirmed",
              subtitle: labelCreatedAt,
              completed: true,
              active: false,
              icon: <CheckCircleRoundedIcon />,
            },
            {
              label: "Shipped",
              subtitle: `Shipped on ${actualShipped}`,
              completed: true,
              active: false,
              icon: <LocalShippingOutlinedIcon />,
            },
            {
              label: "Out for Delivery",
              subtitle: `Expected by ${expectedDD}`,
              completed: false,
              active: true,
              icon: <DeliveryDiningOutlinedIcon />,
            },
            {
              label: "Delivered",
              subtitle: `Expected by ${expectedDD}`,
              completed: false,
              active: false,
              icon: <TaskAltRoundedIcon />,
            },
          ],
        };
      }

      case "OUT FOR DELIVERY":
        return {
          label: "Out for Delivery",
          badgeColor: "#d97706",
          badgeBg: "#fffbeb",
          badgeBorder: "#fde68a",
          activeStep: 2,
          expectedBanner: "🚚 Arriving Today! Your package is out with the delivery executive.",
          steps: [
            {
              label: "Confirmed",
              subtitle: labelCreatedAt,
              completed: true,
              active: false,
              icon: <CheckCircleRoundedIcon />,
            },
            {
              label: "Shipped",
              subtitle: order.shippedAt ? formatDateToCustomFormat(getDateFromCreatedAt(order.shippedAt)) : "Completed",
              completed: true,
              active: false,
              icon: <LocalShippingOutlinedIcon />,
            },
            {
              label: "Out for Delivery",
              subtitle: order.ofdAt ? formatDateToCustomFormat(getDateFromCreatedAt(order.ofdAt)) : "Today",
              completed: true,
              active: false,
              icon: <DeliveryDiningOutlinedIcon />,
            },
            {
              label: "Delivered",
              subtitle: "Expected today",
              completed: false,
              active: true,
              icon: <TaskAltRoundedIcon />,
            },
          ],
        };

      case "DELIVERED": {
        const actualDelivered = order.deliveredAt
          ? formatDateToCustomFormat(getDateFromCreatedAt(order.deliveredAt))
          : "Delivered";
        return {
          label: "Delivered",
          badgeColor: "#047857",
          badgeBg: "#ecfdf5",
          badgeBorder: "#a7f3d0",
          activeStep: 3,
          expectedBanner: `🎉 Package was successfully delivered on ${actualDelivered}`,
          steps: [
            {
              label: "Confirmed",
              subtitle: labelCreatedAt,
              completed: true,
              active: false,
              icon: <CheckCircleRoundedIcon />,
            },
            {
              label: "Shipped",
              subtitle: order.shippedAt ? formatDateToCustomFormat(getDateFromCreatedAt(order.shippedAt)) : "Completed",
              completed: true,
              active: false,
              icon: <LocalShippingOutlinedIcon />,
            },
            {
              label: "Out for Delivery",
              subtitle: order.ofdAt ? formatDateToCustomFormat(getDateFromCreatedAt(order.ofdAt)) : "Completed",
              completed: true,
              active: false,
              icon: <DeliveryDiningOutlinedIcon />,
            },
            {
              label: "Delivered",
              subtitle: `Delivered on ${actualDelivered}`,
              completed: true,
              active: false,
              icon: <TaskAltRoundedIcon />,
            },
          ],
        };
      }

      case "CANCELLED BY BUYER":
      case "CANCELLED BY SELLER":
      case "CANCELLED": {
        const isSeller = statusRaw.includes("SELLER");
        const cancelDate = order.cancelledAt
          ? formatDateToCustomFormat(getDateFromCreatedAt(order.cancelledAt))
          : "N/A";
        return {
          label: isSeller ? "Cancelled by Seller" : "Cancelled",
          badgeColor: "#b91c1c",
          badgeBg: "#fef2f2",
          badgeBorder: "#fecaca",
          activeStep: 1,
          expectedBanner: `Order was cancelled on ${cancelDate}.`,
          steps: [
            {
              label: "Confirmed",
              subtitle: labelCreatedAt,
              completed: true,
              active: false,
              icon: <CheckCircleRoundedIcon />,
            },
            {
              label: isSeller ? "Cancelled by Seller" : "Cancelled",
              subtitle: `Cancelled on ${cancelDate}`,
              completed: true,
              active: false,
              isCancelled: true,
              icon: <CancelRoundedIcon />,
            },
          ],
        };
      }

      case "REFUNDED": {
        const refDate = order.refundedAt
          ? formatDateToCustomFormat(getDateFromCreatedAt(order.refundedAt))
          : "Processed";
        return {
          label: "Refunded",
          badgeColor: "#4f46e5",
          badgeBg: "#eef2ff",
          badgeBorder: "#c7d2fe",
          activeStep: 1,
          expectedBanner: `Refund of ₹${order.amount} was processed on ${refDate}.`,
          steps: [
            {
              label: "Confirmed",
              subtitle: labelCreatedAt,
              completed: true,
              active: false,
              icon: <CheckCircleRoundedIcon />,
            },
            {
              label: "Refunded",
              subtitle: `Refunded on ${refDate}`,
              completed: true,
              active: false,
              icon: <CheckCircleRoundedIcon />,
            },
          ],
        };
      }

      default:
        return {
          label: order.status || "Placed",
          badgeColor: "#047857",
          badgeBg: "#ecfdf5",
          badgeBorder: "#a7f3d0",
          activeStep: 0,
          expectedBanner: `Estimated Delivery by ${expectedDD}`,
          steps: [
            {
              label: "Confirmed",
              subtitle: labelCreatedAt,
              completed: true,
              active: true,
              icon: <CheckCircleRoundedIcon />,
            },
          ],
        };
    }
  }, [order]);

  // Loading Skeleton State
  if (fetchingOrder) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, minHeight: "80vh" }}>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={220} height={32} />
          <Skeleton variant="text" width={320} height={24} />
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 3, mb: 3 }} />
            <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 3 }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 3, mb: 3 }} />
            <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 3 }} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  // Not Found State
  if (!order) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: "center" }}>
        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: 4,
            border: "1px solid #e2e8f0",
            bgcolor: "#ffffff",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: "rgba(7, 94, 84, 0.08)",
              color: BRAND_COLOR,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <ShoppingBagOutlinedIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}>
            Order Details Not Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
            We couldn't retrieve the details for this order. It may have been removed, or you might have arrived here via an incomplete link.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              onClick={() => navigate("/userOrders")}
              sx={{
                bgcolor: BRAND_COLOR,
                "&:hover": { bgcolor: "#004d40" },
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              View My Orders
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/collections")}
              sx={{
                borderColor: "#cbd5e1",
                color: "#475569",
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Continue Shopping
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  const items = Array.isArray(order.items) ? order.items : [];
  const itemsSubtotal = items.reduce(
    (sum, item) => sum + Number(item.listPrice || item.sellingPrice || 0) * Number(item.quantity || 1),
    0
  );
  const shippingCost = Number(order.shippingCost || 0);
  const codCharges = Number(order.codCharges || 0);
  const couponDiscount = Number(
    order.couponApplied?.amount ??
    order.couponApplied?.discount ??
    order.couponApplied?.discountAmount ??
    0
  );
  const grandTotal = Number(order.amount ?? (itemsSubtotal + shippingCost + codCharges - couponDiscount));
  const isPaymentPending = String(order.status || "").trim().toUpperCase() === "CREATED";
  const courier = order.courierDetails || {};
  const hasCourierInfo = Boolean(courier.partner || courier.trackingId);

  return (
    <>
      {/* Print Specific CSS */}
      <style>
        {`
          @media print {
            nav, header, footer, .no-print, .site-header, .site-footer, .MuiAppBar-root {
              display: none !important;
            }
            body {
              background: #ffffff !important;
              color: #000000 !important;
            }
            .order-page-container {
              max-width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .print-card {
              border: 1px solid #cbd5e1 !important;
              box-shadow: none !important;
              break-inside: avoid;
            }
          }
        `}
      </style>

      <Box
        className="order-page-container"
        sx={{
          bgcolor: "#f8fafc",
          minHeight: "100vh",
          py: { xs: 2, sm: 4 },
        }}
      >
        <Container maxWidth="lg">
          {/* Breadcrumbs & Navigation */}
          <Box className="no-print" sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
              <IconButton
                onClick={() => navigate(-1)}
                size="small"
                aria-label="Go back"
                sx={{
                  bgcolor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  "&:hover": { bgcolor: "#f1f5f9" },
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Breadcrumbs separator="›" aria-label="breadcrumb">
                <MuiLink
                  component={Link}
                  to="/"
                  underline="hover"
                  color="text.secondary"
                  sx={{ fontSize: "0.875rem" }}
                >
                  Home
                </MuiLink>
                <MuiLink
                  component={Link}
                  to="/userOrders"
                  underline="hover"
                  color="text.secondary"
                  sx={{ fontSize: "0.875rem" }}
                >
                  My Orders
                </MuiLink>
                <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
                  Order #{order.id}
                </Typography>
              </Breadcrumbs>
            </Stack>
          </Box>

          {/* Header Card: Order ID, Date, Status, and Top Actions */}
          <Paper
            className="print-card"
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3 },
              mb: 3,
              borderRadius: 3.5,
              border: "1px solid #e2e8f0",
              bgcolor: "#ffffff",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={7}>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: "#0f172a",
                        fontSize: { xs: "1.25rem", sm: "1.5rem" },
                        letterSpacing: "-0.02em",
                      }}
                    >
                      Order #{order.id}
                    </Typography>
                    <Tooltip title={copiedKey === "orderId" ? "Copied!" : "Copy Order ID"}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(order.id, "orderId", "Order ID")}
                        aria-label="Copy Order ID"
                        sx={{
                          color: "#64748b",
                          bgcolor: "#f1f5f9",
                          "&:hover": { bgcolor: "#e2e8f0", color: BRAND_COLOR },
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "20px",
                        bgcolor: statusMeta?.badgeBg || "#f1f5f9",
                        color: statusMeta?.badgeColor || "#334155",
                        border: `1px solid ${statusMeta?.badgeBorder || "#e2e8f0"}`,
                        fontWeight: 700,
                        fontSize: "0.8125rem",
                      }}
                    >
                      {statusMeta?.label || order.status}
                    </Box>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Placed on {formatDateToCustomFormat(getDateFromCreatedAt(order.createdAt))}
                  </Typography>
                </Stack>
              </Grid>

              {/* Action Buttons in Header */}
              <Grid item xs={12} md={5} className="no-print">
                <Stack
                  direction="row"
                  spacing={1.5}
                  justifyContent={{ xs: "flex-start", md: "flex-end" }}
                  flexWrap="wrap"
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<HelpOutlineIcon />}
                    onClick={() => setOpenHelpDialog(true)}
                    sx={{
                      borderColor: "#cbd5e1",
                      color: "#334155",
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: 2,
                      "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
                    }}
                  >
                    Need Help?
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            {/* Estimated Delivery / Status Highlight Banner */}
            {statusMeta?.expectedBanner && (
              <Box
                sx={{
                  mt: 2.5,
                  p: 1.75,
                  borderRadius: 2.5,
                  bgcolor:
                    order.status?.toUpperCase() === "DELIVERED"
                      ? "rgba(4, 120, 87, 0.08)"
                      : order.status?.toUpperCase()?.includes("CANCEL")
                      ? "rgba(185, 28, 28, 0.08)"
                      : "rgba(7, 94, 84, 0.08)",
                  border: `1px solid ${
                    order.status?.toUpperCase() === "DELIVERED"
                      ? "rgba(4, 120, 87, 0.2)"
                      : order.status?.toUpperCase()?.includes("CANCEL")
                      ? "rgba(185, 28, 28, 0.2)"
                      : "rgba(7, 94, 84, 0.2)"
                  }`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color:
                      order.status?.toUpperCase() === "DELIVERED"
                        ? "#047857"
                        : order.status?.toUpperCase()?.includes("CANCEL")
                        ? "#b91c1c"
                        : BRAND_COLOR,
                  }}
                >
                  {statusMeta.expectedBanner}
                </Typography>
              </Box>
            )}

            {/* Payment Pending Alert Banner with Pay Now button */}
            {isPaymentPending && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2.5,
                  bgcolor: "#fffbeb",
                  border: "1px solid #fde68a",
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#92400e" }}>
                    Payment Pending for this order
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#b45309", mt: 0.25 }}>
                    Please complete your payment of &#8377;{grandTotal} to confirm and ship your order.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={retryPayment}
                  disabled={isPaymentProcessing(order)}
                  sx={{
                    bgcolor: BRAND_COLOR,
                    "&:hover": { bgcolor: "#004d40" },
                    textTransform: "none",
                    fontWeight: 700,
                    px: 3,
                    borderRadius: 2,
                    flexShrink: 0,
                  }}
                >
                  Pay &#8377;{grandTotal} Now
                </Button>
              </Box>
            )}
          </Paper>

          {/* Main 2-Column Responsive Layout */}
          <Grid container spacing={3}>
            {/* LEFT COLUMN: Tracking & Line Items */}
            <Grid item xs={12} md={8}>
              {/* Order Tracking Progress Card */}
              <Paper
                className="print-card"
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  mb: 3,
                  borderRadius: 3.5,
                  border: "1px solid #e2e8f0",
                  bgcolor: "#ffffff",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#1e293b",
                    mb: 3,
                    fontSize: "1.1rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <LocalShippingOutlinedIcon sx={{ color: BRAND_COLOR }} />
                  Order Progress
                </Typography>

                {/* Responsive Stepper: Horizontal on desktop/tablet, Vertical on mobile */}
                {isMobile ? (
                  /* Mobile Vertical Timeline */
                  <Stack spacing={2.5}>
                    {statusMeta?.steps?.map((step, idx) => {
                      const isLast = idx === statusMeta.steps.length - 1;
                      const isPassed = step.completed || step.active;
                      return (
                        <Box key={idx} sx={{ display: "flex", gap: 2, position: "relative" }}>
                          {/* Connector Line */}
                          {!isLast && (
                            <Box
                              sx={{
                                position: "absolute",
                                left: 18,
                                top: 36,
                                bottom: -16,
                                width: 2,
                                bgcolor: step.completed ? BRAND_COLOR : "#e2e8f0",
                                zIndex: 0,
                              }}
                            />
                          )}

                          {/* Step Icon Badge */}
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              bgcolor: step.isCancelled
                                ? "#fee2e2"
                                : step.completed
                                ? BRAND_COLOR
                                : step.active
                                ? "rgba(7, 94, 84, 0.12)"
                                : "#f1f5f9",
                              color: step.isCancelled
                                ? "#dc2626"
                                : step.completed
                                ? "#ffffff"
                                : step.active
                                ? BRAND_COLOR
                                : "#94a3b8",
                              border: `2px solid ${
                                step.isCancelled
                                  ? "#ef4444"
                                  : step.completed
                                  ? BRAND_COLOR
                                  : step.active
                                  ? BRAND_COLOR
                                  : "#e2e8f0"
                              }`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              zIndex: 1,
                            }}
                          >
                            {React.cloneElement(step.icon, { sx: { fontSize: 18 } })}
                          </Box>

                          {/* Step Details */}
                          <Box sx={{ pt: 0.5, flex: 1 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: isPassed ? 700 : 500,
                                color: step.isCancelled
                                  ? "#dc2626"
                                  : isPassed
                                  ? "#0f172a"
                                  : "#64748b",
                              }}
                            >
                              {step.label}
                            </Typography>
                            {step.subtitle && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                                {step.subtitle}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  /* Desktop / Tablet Horizontal Timeline */
                  <Box sx={{ px: 2, py: 1 }}>
                    <Grid container alignItems="flex-start" justifyContent="space-between">
                      {statusMeta?.steps?.map((step, idx) => {
                        const isLast = idx === statusMeta.steps.length - 1;
                        const isPassed = step.completed || step.active;
                        return (
                          <React.Fragment key={idx}>
                            <Grid
                              item
                              sx={{
                                textAlign: "center",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                maxWidth: 130,
                              }}
                            >
                              {/* Step Icon Badge */}
                              <Box
                                sx={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: "50%",
                                  bgcolor: step.isCancelled
                                    ? "#fee2e2"
                                    : step.completed
                                    ? BRAND_COLOR
                                    : step.active
                                    ? "rgba(7, 94, 84, 0.12)"
                                    : "#f8fafc",
                                  color: step.isCancelled
                                    ? "#dc2626"
                                    : step.completed
                                    ? "#ffffff"
                                    : step.active
                                    ? BRAND_COLOR
                                    : "#94a3b8",
                                  border: `2.5px solid ${
                                    step.isCancelled
                                      ? "#ef4444"
                                      : step.completed
                                      ? BRAND_COLOR
                                      : step.active
                                      ? BRAND_COLOR
                                      : "#cbd5e1"
                                  }`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mb: 1.5,
                                  boxShadow: step.active ? "0 0 0 4px rgba(7, 94, 84, 0.15)" : "none",
                                  transition: "all 0.2s ease",
                                }}
                              >
                                {React.cloneElement(step.icon, { sx: { fontSize: 22 } })}
                              </Box>

                              {/* Label */}
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: isPassed ? 700 : 500,
                                  color: step.isCancelled
                                    ? "#dc2626"
                                    : isPassed
                                    ? "#0f172a"
                                    : "#64748b",
                                  lineHeight: 1.25,
                                  mb: 0.5,
                                }}
                              >
                                {step.label}
                              </Typography>

                              {/* Subtitle / Timestamp */}
                              {step.subtitle && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ lineHeight: 1.3, fontSize: "0.75rem" }}
                                >
                                  {step.subtitle}
                                </Typography>
                              )}
                            </Grid>

                            {/* Connecting Line between steps */}
                            {!isLast && (
                              <Box
                                sx={{
                                  flex: 1,
                                  height: 3,
                                  bgcolor: step.completed ? BRAND_COLOR : "#e2e8f0",
                                  mt: 2.6,
                                  mx: 1,
                                  borderRadius: 1,
                                }}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </Grid>
                  </Box>
                )}
              </Paper>

              {/* Courier & Tracking Details Card (if shipped / courier details exist) */}
              {hasCourierInfo && (
                <Paper
                  className="print-card"
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    mb: 3,
                    borderRadius: 3.5,
                    border: "1px solid #e2e8f0",
                    bgcolor: "#ffffff",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#1e293b",
                      mb: 2,
                      fontSize: "1.1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <LocalShippingOutlinedIcon sx={{ color: BRAND_COLOR }} />
                    Shipment & Courier Information
                  </Typography>

                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Courier Partner
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b" }}>
                        {courier.partner || "Standard Courier"}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={5}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        AWB / Tracking Number
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body1" sx={{ fontWeight: 700, fontFamily: "monospace" }}>
                          {courier.trackingId || "N/A"}
                        </Typography>
                        {courier.trackingId && (
                          <Tooltip title={copiedKey === "trackingId" ? "Copied!" : "Copy Tracking ID"}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(courier.trackingId, "trackingId", "Tracking number")}
                            >
                              <ContentCopyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Grid>

                    <Grid item xs={12} sm={3} sx={{ textAlign: { xs: "left", sm: "right" } }}>
                      {courier.trackingUrl ? (
                        <Button
                          variant="outlined"
                          size="small"
                          href={courier.trackingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          endIcon={<OpenInNewIcon />}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 2,
                            borderColor: BRAND_COLOR,
                            color: BRAND_COLOR,
                            "&:hover": { borderColor: "#004d40", bgcolor: "rgba(7, 94, 84, 0.04)" },
                          }}
                        >
                          Track Package
                        </Button>
                      ) : (
                        courier.partner && (
                          <Chip
                            label="In Transit"
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        )
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* Items in Order Card */}
              <Paper
                className="print-card"
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: 3.5,
                  border: "1px solid #e2e8f0",
                  bgcolor: "#ffffff",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#1e293b",
                      fontSize: "1.1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <ShoppingBagOutlinedIcon sx={{ color: BRAND_COLOR }} />
                    Items in this Order ({items.length})
                  </Typography>
                </Stack>

                <Stack spacing={2} divider={<Divider sx={{ borderColor: "#f1f5f9" }} />}>
                  {items.map((item, index) => {
                    const itemImage =
                      item.images?.[0]?.url || item.image || item.thumbnail || "/logo192.png";
                    const itemPrice = Number(item.listPrice || item.sellingPrice || 0);
                    const itemQty = Number(item.quantity || 1);
                    const itemTotal = itemPrice * itemQty;
                    const itemLink = item.parentCategory?.id
                      ? `/collections/${item.parentCategory.id}/${item.id}`
                      : item.categories?.[0]
                      ? `/collections/${item.categories[0]}/${item.id}`
                      : "/collections";

                    return (
                      <Box
                        key={item.id || index}
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "center",
                          py: 1,
                        }}
                      >
                        {/* Product Image Thumbnail */}
                        <Box
                          component={Link}
                          to={itemLink}
                          sx={{
                            width: { xs: 68, sm: 84 },
                            height: { xs: 68, sm: 84 },
                            borderRadius: 2.5,
                            border: "1px solid #e2e8f0",
                            bgcolor: "#f8fafc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 0.5,
                            flexShrink: 0,
                            textDecoration: "none",
                            overflow: "hidden",
                            "&:hover img": { transform: "scale(1.05)" },
                          }}
                        >
                          <Box
                            component="img"
                            src={itemImage}
                            alt={item.title || "Product"}
                            sx={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                              transition: "transform 0.2s ease",
                            }}
                          />
                        </Box>

                        {/* Title & Product Info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            component={Link}
                            to={itemLink}
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              color: "#0f172a",
                              textDecoration: "none",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              fontSize: { xs: "0.9rem", sm: "1rem" },
                              lineHeight: 1.35,
                              "&:hover": { color: BRAND_COLOR },
                            }}
                          >
                            {item.title}
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            flexWrap="wrap"
                            sx={{ mt: 0.75 }}
                          >
                            <Chip
                              label={`Qty: ${itemQty}`}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                bgcolor: "#f1f5f9",
                                color: "#475569",
                              }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              &#8377;{itemPrice} each
                            </Typography>
                          </Stack>
                        </Box>

                        {/* Item Total & Buy Again Button */}
                        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              color: "#0f172a",
                              fontSize: { xs: "0.95rem", sm: "1.05rem" },
                            }}
                          >
                            &#8377;{itemTotal}
                          </Typography>

                          <Button
                            className="no-print"
                            size="small"
                            variant="text"
                            onClick={() => handleBuyAgain(item)}
                            sx={{
                              mt: 0.5,
                              textTransform: "none",
                              color: BRAND_COLOR,
                              fontWeight: 600,
                              p: 0.5,
                              minWidth: "auto",
                              fontSize: "0.8125rem",
                              "&:hover": { bgcolor: "rgba(7, 94, 84, 0.06)" },
                            }}
                          >
                            Buy Again
                          </Button>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Paper>
            </Grid>

            {/* RIGHT COLUMN: Order Summary, Delivery Address, Payment, Support */}
            <Grid item xs={12} md={4}>
              <Stack spacing={3}>
                {/* 1. ORDER SUMMARY CARD */}
                <Paper
                  className="print-card"
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 3.5,
                    border: "1px solid #e2e8f0",
                    bgcolor: "#ffffff",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#1e293b",
                      mb: 2.5,
                      fontSize: "1.1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <ReceiptLongOutlinedIcon sx={{ color: BRAND_COLOR }} />
                    Order Summary
                  </Typography>

                  <Stack spacing={1.5}>
                    {/* Items Subtotal */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Items Subtotal ({items.length} items)
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                        &#8377;{itemsSubtotal}
                      </Typography>
                    </Box>

                    {/* Shipping Fee */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Delivery / Shipping
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: shippingCost === 0 ? "#16a34a" : "#1e293b",
                        }}
                      >
                        {shippingCost === 0 ? "FREE" : `₹${shippingCost}`}
                      </Typography>
                    </Box>

                    {/* COD Charges if applicable */}
                    {codCharges > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          COD Charges
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                          &#8377;{codCharges}
                        </Typography>
                      </Box>
                    )}

                    {/* Coupon Discount */}
                    {couponDiscount > 0 && (
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2" sx={{ color: "#16a34a", fontWeight: 500 }}>
                          Coupon Discount{" "}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              bgcolor: "#dcfce7",
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 1,
                              fontWeight: 700,
                              color: "#15803d",
                            }}
                          >
                            {order.couponApplied?.couponCode || order.couponApplied?.code || "DISCOUNT"}
                          </Typography>
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#16a34a" }}>
                          - &#8377;{couponDiscount}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ my: 1, borderColor: "#e2e8f0" }} />

                    {/* Grand Total */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a" }}>
                        Total Amount
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 800, color: BRAND_COLOR, fontSize: "1.35rem" }}
                      >
                        &#8377;{grandTotal}
                      </Typography>
                    </Box>

                    {/* Savings Highlight */}
                    {couponDiscount > 0 && (
                      <Box
                        sx={{
                          p: 1.25,
                          mt: 1,
                          bgcolor: "#f0fdf4",
                          border: "1px solid #bbf7d0",
                          borderRadius: 2,
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#166534" }}>
                          🎉 You saved &#8377;{couponDiscount} on this order!
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Paper>

                {/* 2. DELIVERY ADDRESS CARD */}
                <Paper
                  className="print-card"
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 3.5,
                    border: "1px solid #e2e8f0",
                    bgcolor: "#ffffff",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#1e293b",
                      mb: 2,
                      fontSize: "1.1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <LocationOnOutlinedIcon sx={{ color: BRAND_COLOR }} />
                    Delivery Address
                  </Typography>

                  {order.address ? (
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                        {order.address.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                        {order.address.address}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {order.address.city}, {order.address.state} - {order.address.pincode}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 0.5 }}>
                        <PhoneOutlinedIcon sx={{ fontSize: 16, color: "#64748b" }} />
                        <Typography
                          component="a"
                          href={`tel:${order.address.mobile}`}
                          variant="body2"
                          sx={{
                            color: "#0f172a",
                            fontWeight: 600,
                            textDecoration: "none",
                            "&:hover": { color: BRAND_COLOR },
                          }}
                        >
                          {order.address.mobile}
                          {order.address.alternateMobile && `, ${order.address.alternateMobile}`}
                        </Typography>
                      </Box>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No shipping address recorded for this order.
                    </Typography>
                  )}
                </Paper>

                {/* 3. PAYMENT INFORMATION CARD */}
                <Paper
                  className="print-card"
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 3.5,
                    border: "1px solid #e2e8f0",
                    bgcolor: "#ffffff",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#1e293b",
                      mb: 2,
                      fontSize: "1.1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CreditCardOutlinedIcon sx={{ color: BRAND_COLOR }} />
                    Payment Details
                  </Typography>

                  <Stack spacing={1.5}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Payment Method
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                        {order.payment?.method?.toUpperCase() === "ONLINE" || order.payment?.payment_id
                          ? "Online (Razorpay / UPI / Card)"
                          : order.paymentMethod?.toUpperCase() === "COD" || codCharges > 0
                          ? "Cash on Delivery (COD)"
                          : "Online Payment"}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="body2" color="text.secondary">
                        Payment Status
                      </Typography>
                      <Chip
                        label={
                          order.status?.toUpperCase() === "CREATED"
                            ? isPaymentProcessing(order)
                              ? "Processing"
                              : "Pending"
                            : order.status?.toUpperCase() === "REFUNDED"
                            ? "Refunded"
                            : "Paid"
                        }
                        size="small"
                        color={
                          order.status?.toUpperCase() === "CREATED"
                            ? isPaymentProcessing(order)
                              ? "warning"
                              : "error"
                            : order.status?.toUpperCase() === "REFUNDED"
                            ? "info"
                            : "success"
                        }
                        variant="filled"
                        sx={{ fontWeight: 700, height: 22, fontSize: "0.75rem" }}
                      />
                    </Box>

                    {order.payment?.payment_id && (
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          Transaction ID
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                            {order.payment.payment_id}
                          </Typography>
                          <Tooltip title={copiedKey === "paymentId" ? "Copied!" : "Copy Transaction ID"}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(order.payment.payment_id, "paymentId", "Transaction ID")}
                            >
                              <ContentCopyIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Paper>

                {/* 4. NEED HELP / CUSTOMER SUPPORT CARD */}
                <Paper
                  className="no-print"
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 3.5,
                    border: "1px solid #e2e8f0",
                    bgcolor: "#ffffff",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#1e293b",
                      mb: 1.5,
                      fontSize: "1.1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <ShieldOutlinedIcon sx={{ color: BRAND_COLOR }} />
                    Need Assistance?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
                    Have questions about this order, delivery, or returns? We're here to help.
                  </Typography>

                  <Stack spacing={1.5}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<WhatsAppIcon sx={{ color: "#25d366" }} />}
                      href={`https://api.whatsapp.com/send/?phone=${WHATSAPP_PHONE}&text=${encodeURIComponent(
                        `Hi Inproveda Team, I need assistance regarding Order #${order.id}.`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: "#e2e8f0",
                        color: "#1e293b",
                        justifyContent: "flex-start",
                        borderRadius: 2,
                        "&:hover": { borderColor: "#25d366", bgcolor: "rgba(37, 211, 102, 0.05)" },
                      }}
                    >
                      Chat on WhatsApp
                    </Button>

                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<EmailOutlinedIcon sx={{ color: BRAND_COLOR }} />}
                      href={`mailto:orders@inproveda.com?subject=${encodeURIComponent(
                        `Query regarding Order #${order.id}`
                      )}&body=${encodeURIComponent(
                        `Hi Inproveda Support Team,\n\nI need help with my Order #${order.id}.\n\nDetails:\n`
                      )}`}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: "#e2e8f0",
                        color: "#1e293b",
                        justifyContent: "flex-start",
                        borderRadius: 2,
                        "&:hover": { borderColor: BRAND_COLOR, bgcolor: "rgba(7, 94, 84, 0.05)" },
                      }}
                    >
                      Email orders@inproveda.com
                    </Button>

                    <Stack direction="row" spacing={1} justifyContent="center" sx={{ pt: 1 }}>
                      <MuiLink
                        component={Link}
                        to="/refund-policy"
                        underline="hover"
                        variant="caption"
                        sx={{ color: "#64748b", fontWeight: 600 }}
                      >
                        Refund Policy
                      </MuiLink>
                      <Typography variant="caption" color="text.disabled">
                        •
                      </Typography>
                      <MuiLink
                        component={Link}
                        to="/shipping-policy"
                        underline="hover"
                        variant="caption"
                        sx={{ color: "#64748b", fontWeight: 600 }}
                      >
                        Shipping Policy
                      </MuiLink>
                    </Stack>
                  </Stack>
                </Paper>
              </Stack>
            </Grid>
          </Grid>
        </Container>


        {/* Enhanced Need Help Dialog */}
        <Dialog
          open={openHelpDialog}
          onClose={() => setOpenHelpDialog(false)}
          aria-labelledby="need-help-dialog-title"
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: { xs: 3, sm: 4 },
              p: { xs: 0.5, sm: 1 },
              overflow: "hidden",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            },
          }}
        >
          {/* Top Header with Icon, Title, and Close Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              p: { xs: 2, sm: 2.5 },
              pb: 1,
            }}
          >
            <Stack direction="row" spacing={1.75} alignItems="center">
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: "14px",
                  bgcolor: "rgba(7, 94, 84, 0.1)",
                  color: BRAND_COLOR,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <SupportAgentRoundedIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography
                  id="need-help-dialog-title"
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: "#0f172a",
                    fontSize: { xs: "1.1rem", sm: "1.25rem" },
                    lineHeight: 1.25,
                  }}
                >
                  Order Support & Assistance
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: "0.85rem" }}>
                  Need help with Order #{order.id}? Choose an option below.
                </Typography>
              </Box>
            </Stack>

            <IconButton
              size="small"
              onClick={() => setOpenHelpDialog(false)}
              aria-label="Close help dialog"
              sx={{
                color: "#94a3b8",
                "&:hover": { color: "#0f172a", bgcolor: "#f1f5f9" },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          <DialogContent sx={{ p: { xs: 2, sm: 2.5 }, pt: 0.5 }}>
            {/* Order Context Strip */}
            <Box
              sx={{
                mb: 2.5,
                p: 1.5,
                borderRadius: 2.5,
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
                  ORDER #{order.id}
                </Typography>
                <Chip
                  label={statusMeta?.label || order.status}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    bgcolor: statusMeta?.badgeBg || "#f1f5f9",
                    color: statusMeta?.badgeColor || "#334155",
                    border: `1px solid ${statusMeta?.badgeBorder || "#e2e8f0"}`,
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: BRAND_COLOR }}>
                Total: &#8377;{grandTotal}
              </Typography>
            </Box>

            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#64748b",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                display: "block",
                mb: 1.5,
              }}
            >
              Get in Touch with Us
            </Typography>

            {/* Support Channels List */}
            <Stack spacing={1.5} sx={{ mb: 2.5 }}>
              {/* WhatsApp Card (Featured) */}
              <Paper
                component="a"
                href={`https://api.whatsapp.com/send/?phone=${WHATSAPP_PHONE}&text=${encodeURIComponent(
                  `Hi Inproveda Team, I need assistance regarding Order #${order.id} (Status: ${order.status}, Amount: ₹${grandTotal}).`
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                elevation={0}
                sx={{
                  p: 1.75,
                  borderRadius: 2.5,
                  border: "1.5px solid #25d366",
                  bgcolor: "rgba(37, 211, 102, 0.04)",
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "rgba(37, 211, 102, 0.1)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(37, 211, 102, 0.15)",
                  },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "12px",
                      bgcolor: "#25d366",
                      color: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <WhatsAppIcon sx={{ fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                        Chat on WhatsApp
                      </Typography>
                      <Chip
                        label="Instant"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.65rem",
                          fontWeight: 800,
                          bgcolor: "#25d366",
                          color: "#ffffff",
                        }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                      Quickest replies • Available 9:00 AM – 8:00 PM
                    </Typography>
                  </Box>
                </Stack>
                <ChevronRightRoundedIcon sx={{ color: "#25d366" }} />
              </Paper>

              {/* Call Support Card */}
              <Paper
                component="a"
                href={`tel:+${WHATSAPP_PHONE}`}
                elevation={0}
                sx={{
                  p: 1.75,
                  borderRadius: 2.5,
                  border: "1px solid #e2e8f0",
                  bgcolor: "#ffffff",
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: BRAND_COLOR,
                    bgcolor: "rgba(7, 94, 84, 0.03)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "12px",
                      bgcolor: "rgba(7, 94, 84, 0.08)",
                      color: BRAND_COLOR,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <CallOutlinedIcon sx={{ fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                      Call Customer Care
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                      +91 94111 74186 • Mon – Sat (10:00 AM – 6:00 PM)
                    </Typography>
                  </Box>
                </Stack>
                <ChevronRightRoundedIcon sx={{ color: "#94a3b8" }} />
              </Paper>

              {/* Email Support Card */}
              <Paper
                component="a"
                href={`mailto:orders@inproveda.com?subject=${encodeURIComponent(
                  `Assistance with Order #${order.id}`
                )}&body=${encodeURIComponent(
                  `Hi Inproveda Support Team,\n\nI need assistance regarding Order #${order.id}.\n\nName: ${order.address?.name || ""}\nPhone: ${order.address?.mobile || ""}\n\nQuery / Issue:\n`
                )}`}
                elevation={0}
                sx={{
                  p: 1.75,
                  borderRadius: 2.5,
                  border: "1px solid #e2e8f0",
                  bgcolor: "#ffffff",
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: BRAND_COLOR,
                    bgcolor: "rgba(7, 94, 84, 0.03)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "12px",
                      bgcolor: "rgba(7, 94, 84, 0.08)",
                      color: BRAND_COLOR,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <EmailOutlinedIcon sx={{ fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                      Email Support (orders@inproveda.com)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                      Detailed queries • Response within 24 hours
                    </Typography>
                  </Box>
                </Stack>
                <ChevronRightRoundedIcon sx={{ color: "#94a3b8" }} />
              </Paper>
            </Stack>

            {/* Self-Service & Policies Section */}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#64748b",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                display: "block",
                mb: 1.5,
              }}
            >
              Self-Service & Help Guides
            </Typography>

            <Grid container spacing={1.5} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={6}>
                <Paper
                  component={Link}
                  to="/refund-policy"
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    textDecoration: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    color: "inherit",
                    "&:hover": {
                      borderColor: BRAND_COLOR,
                      bgcolor: "#f8fafc",
                    },
                  }}
                >
                  <AssignmentReturnOutlinedIcon sx={{ fontSize: 20, color: BRAND_COLOR }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#0f172a", display: "block" }}>
                      Returns & Refunds
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", fontSize: "0.7rem" }}>
                      7-day return policy
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper
                  component={Link}
                  to="/shipping-policy"
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    textDecoration: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    color: "inherit",
                    "&:hover": {
                      borderColor: BRAND_COLOR,
                      bgcolor: "#f8fafc",
                    },
                  }}
                >
                  <LocalShippingOutlinedIcon sx={{ fontSize: 20, color: BRAND_COLOR }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#0f172a", display: "block" }}>
                      Shipping Timelines
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", fontSize: "0.7rem" }}>
                      Delivery FAQs & details
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions
            sx={{
              px: { xs: 2, sm: 2.5 },
              py: 1.5,
              borderTop: "1px solid #f1f5f9",
              justifyContent: "space-between",
              bgcolor: "#f8fafc",
            }}
          >
            <Stack direction="row" spacing={0.75} alignItems="center">
              <ShieldOutlinedIcon sx={{ fontSize: 16, color: BRAND_COLOR }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                100% Genuine Support Guarantee
              </Typography>
            </Stack>
            <Button
              onClick={() => setOpenHelpDialog(false)}
              size="small"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#475569",
                borderRadius: 1.5,
                px: 2,
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Global Loading overlay during action */}
        {loadingAction && <Loader />}

        {/* Toast / Snackbar Notifications */}
        <CustomisedSnackbar
          open={snackbar.open}
          message={snackbar.message}
          title={snackbar.title}
          severity={snackbar.severity}
          handleClose={handleCloseSnackbar}
        />
      </Box>
    </>
  );
};

export default OrderDetailPage;
